import * as yup from "yup";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { AddressDTO, AdminAddressDTO } from "@/api/data-contracts";
import { Constants } from "@/api/constants";
import { ZIP_CODE_REGEX } from "@/regex";
import { countryOptions } from "@/datacaches";
import { AdminI18N, CompanyI18N } from "@/translations";

/**
 * Shared Yup schema for the two address-edit dialogs (company detail page and admin address page).
 * Both dialogs validate the identical core address shape (street, house number, zip, city, country,
 * delivery instructions); the admin dialog additionally edits the geo coordinates directly. The two
 * exported hooks below are thin wrappers around one shared shape so validation rules are maintained
 * in exactly one place.
 */

const deliveryInstructionText = (t: TFunction, messageKey: string) =>
    yup
        .string()
        .optional()
        .nullable()
        .max(
            Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
            t(messageKey, { max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX })
        );

const buildDeliveryInstructionSchema = (t: TFunction) =>
    yup.object().shape({
        mondayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        tuesdayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        wednesdayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        thursdayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        fridayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        saturdayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        sundayInstruction: deliveryInstructionText(t, CompanyI18N.form.validation.deliveryInstructionsMax),
        arrivalNotification: deliveryInstructionText(t, CompanyI18N.form.validation.arrivalNotificationMax),
        timeSlotBookingRequired: yup.boolean().optional().nullable()
    });

const buildSharedAddressShape = (t: TFunction) => ({
    street: yup
        .string()
        .required(t(CompanyI18N.form.validation.streetRequired))
        .min(
            Constants.STREET_LENGTH_MIN,
            t(CompanyI18N.form.validation.streetMin, { min: Constants.STREET_LENGTH_MIN })
        )
        .max(
            Constants.STREET_LENGTH_MAX,
            t(CompanyI18N.form.validation.streetMax, { max: Constants.STREET_LENGTH_MAX })
        ),
    houseNumber: yup
        .string()
        .required(t(CompanyI18N.form.validation.houseNumberRequired))
        .min(
            Constants.HOUSE_NUMBER_LENGTH_MIN,
            t(CompanyI18N.form.validation.houseNumberMin, { min: Constants.HOUSE_NUMBER_LENGTH_MIN })
        )
        .max(
            Constants.HOUSE_NUMBER_LENGTH_MAX,
            t(CompanyI18N.form.validation.houseNumberMax, { max: Constants.HOUSE_NUMBER_LENGTH_MAX })
        ),
    zip: yup
        .string()
        .required(t(CompanyI18N.form.validation.postalCodeRequired))
        .min(Constants.ZIP_LENGTH_MIN, t(CompanyI18N.form.validation.postalCodeMin, { min: Constants.ZIP_LENGTH_MIN }))
        .max(Constants.ZIP_LENGTH_MAX, t(CompanyI18N.form.validation.postalCodeMax, { max: Constants.ZIP_LENGTH_MAX }))
        .matches(ZIP_CODE_REGEX, t(CompanyI18N.form.validation.postalCodeInvalid)),
    city: yup
        .string()
        .required(t(CompanyI18N.form.validation.cityRequired))
        .min(Constants.CITY_LENGTH_MIN, t(CompanyI18N.form.validation.cityMin, { min: Constants.CITY_LENGTH_MIN }))
        .max(Constants.CITY_LENGTH_MAX, t(CompanyI18N.form.validation.cityMax, { max: Constants.CITY_LENGTH_MAX })),
    country: yup
        .string()
        .required(t(CompanyI18N.form.validation.countryRequired))
        .oneOf(countryOptions, t(CompanyI18N.form.validation.countryInvalid)),
    deliveryInstruction: buildDeliveryInstructionSchema(t),
    companyAddress: yup.boolean()
});

/**
 * Address-edit schema for the company detail page dialog (no coordinate fields — the regular
 * update path geocodes server-side).
 */
export const useAddressEditSchema = () => {
    const {t} = useTranslation();
    return useMemo(() => yup.object().shape(buildSharedAddressShape(t)) as yup.ObjectSchema<AddressDTO>, [t]);
};

/**
 * Address-edit schema for the admin address page dialog: the shared shape plus the directly
 * editable geo coordinates (admin updates apply coordinates as-is instead of geocoding).
 */
export const useAdminAddressEditSchema = () => {
    const {t} = useTranslation();
    return useMemo(
        () =>
            yup.object().shape({
                ...buildSharedAddressShape(t),
                latitude: yup
                    .number()
                    .required(t(AdminI18N.addressAdmin.validation.latitudeRequired))
                    .typeError(t(AdminI18N.addressAdmin.validation.latitudeRequired))
                    .min(Number(Constants.LATITUDE_MIN), t(AdminI18N.addressAdmin.validation.latitudeRequired))
                    .max(Constants.LATITUDE_MAX, t(AdminI18N.addressAdmin.validation.latitudeRequired)),
                longitude: yup
                    .number()
                    .required(t(AdminI18N.addressAdmin.validation.longitudeRequired))
                    .typeError(t(AdminI18N.addressAdmin.validation.longitudeRequired))
                    .min(Number(Constants.LONGITUDE_MIN), t(AdminI18N.addressAdmin.validation.longitudeRequired))
                    .max(Constants.LONGITUDE_MAX, t(AdminI18N.addressAdmin.validation.longitudeRequired))
            }) as yup.ObjectSchema<AdminAddressDTO>,
        [t]
    );
};
